// The rows an "@" mention offers.
import {
    groupReferenceLabel,
    groupReferenceSubject,
} from '@apps/web/features/phoebe_chat_v3/model/group_reference_label';
import { type ReferenceType } from '@apps/web/features/phoebe_chat_v3/model/reference_tokens';
import { buildOutreachDisplayUnits } from '@apps/web/lib/outreach_shift_ordering';

export const MENTIONABLE_REFERENCE_TYPES = [
    'shift',
    'client',
    'caregiver',
    'group',
] as const;

export type MentionableReferenceType =
    (typeof MENTIONABLE_REFERENCE_TYPES)[number];

export function isMentionableReferenceType(
    type: ReferenceType,
): type is MentionableReferenceType {
    return (MENTIONABLE_REFERENCE_TYPES as ReadonlyArray<string>).includes(
        type,
    );
}

export type MentionCandidate = Readonly<{
    readonly type: MentionableReferenceType;
    readonly id: string;
    /** What the pick inserts; a group's carries its mode. */
    readonly label: string;
    /** The label without the part the row badges separately. */
    readonly subject: string;
    readonly detail: string | null;
    readonly token: string;
    readonly section: 'outreach' | 'other';
    /** "Group N", on the group row and on each of its member shifts. */
    readonly tag: string | null;
    readonly groupMode: 'all' | 'any' | null;
}>;

/** A candidate plus the words it can be found by: a group also answers to
 * its number and its members' dates. */
export type SearchableMentionCandidate = Readonly<{
    readonly candidate: MentionCandidate;
    readonly searchText: string;
}>;

/** Every row but a group: the label is the subject and there is no mode. */
export function plainMentionCandidate(
    fields: Omit<MentionCandidate, 'subject' | 'groupMode'>,
): MentionCandidate {
    return { ...fields, subject: fields.label, groupMode: null };
}

type MentionPerson = Readonly<{
    readonly id: string;
    readonly name: string;
    readonly office_name?: string | null;
}>;

type MentionShift = Readonly<{
    readonly id: string;
    readonly client_id?: string | null;
    readonly patient_name: string;
    readonly shift_start_time: string;
    readonly status: string;
    readonly caregiver?: MentionPerson | null;
}>;

export type OutreachMentionSource = Readonly<{
    readonly agent_generation: 'v2' | 'v3';
    readonly shifts: ReadonlyArray<MentionShift>;
    readonly shift_groups: ReadonlyArray<
        Readonly<{
            readonly id: string;
            readonly shift_ids: ReadonlyArray<string>;
            readonly group_mode: 'all' | 'any';
        }>
    >;
    readonly original_caregivers: Readonly<Record<string, MentionPerson>>;
    /** On a v3 outreach, the caregivers the agent staged. */
    readonly suggested_caregivers?: ReadonlyArray<
        Readonly<{
            readonly caregiver_id: string;
            readonly caregiver_name: string;
        }>
    > | null;
}>;

const FILLED_STATUSES: ReadonlySet<string> = new Set([
    'assigned',
    'assigned_and_confirmed',
    'in_progress',
    'completed',
]);

function formatShiftDate(iso: string, timeZone: string): string {
    return new Intl.DateTimeFormat(undefined, {
        month: 'short',
        day: 'numeric',
        timeZone,
    }).format(new Date(iso));
}

function formatShiftTime(iso: string, timeZone: string): string {
    return new Intl.DateTimeFormat(undefined, {
        hour: 'numeric',
        minute: '2-digit',
        timeZone,
    }).format(new Date(iso));
}

function formatMonth(iso: string, timeZone: string): string {
    return new Intl.DateTimeFormat(undefined, {
        month: 'short',
        timeZone,
    }).format(new Date(iso));
}

function formatDay(iso: string, timeZone: string): string {
    return new Intl.DateTimeFormat(undefined, {
        day: 'numeric',
        timeZone,
    }).format(new Date(iso));
}

/** The days a group covers, which tells two groups for one client apart. */
function formatDateSpan(
    isoDates: ReadonlyArray<string>,
    timeZone: string,
): string | null {
    const first = isoDates[0];
    const last = isoDates[isoDates.length - 1];
    if (first === undefined || last === undefined) {
        return null;
    }
    const firstMonth = formatMonth(first, timeZone);
    const firstDay = formatDay(first, timeZone);
    const lastMonth = formatMonth(last, timeZone);
    const lastDay = formatDay(last, timeZone);
    if (firstMonth === lastMonth && firstDay === lastDay) {
        return `${firstMonth} ${firstDay}`;
    }
    return firstMonth === lastMonth
        ? `${firstMonth} ${firstDay}–${lastDay}`
        : `${firstMonth} ${firstDay}–${lastMonth} ${lastDay}`;
}

/** Beside a shift row's label: its time, or that it is cancelled or who
 * filled it. */
function shiftDetail(
    shift: MentionShift,
    timeZone: string,
    formatName: (name: string | null | undefined) => string,
): string {
    if (shift.status === 'cancelled') {
        return 'cancelled';
    }
    if (FILLED_STATUSES.has(shift.status)) {
        const name = shift.caregiver?.name;
        return name === undefined ? 'filled' : `filled · ${formatName(name)}`;
    }
    return formatShiftTime(shift.shift_start_time, timeZone);
}

/** A shift row, whether the outreach's own or one from the org search. */
export function shiftCandidate(
    shift: MentionShift,
    timeZone: string,
    section: MentionCandidate['section'],
    tag: string | null,
    formatName: (name: string | null | undefined) => string,
): SearchableMentionCandidate {
    const label = `${formatName(shift.patient_name)}, ${formatShiftDate(shift.shift_start_time, timeZone)}`;
    const detail = shiftDetail(shift, timeZone, formatName);
    return {
        candidate: plainMentionCandidate({
            type: 'shift',
            id: shift.id,
            label,
            detail,
            token: `[[shift:${shift.id}]]`,
            section,
            tag,
        }),
        searchText: `${label} ${detail} ${formatShiftTime(shift.shift_start_time, timeZone)}`,
    };
}

/** A caregiver or client row, whether on the outreach or from the org
 * search. */
export function personCandidate(
    type: 'caregiver' | 'client',
    person: MentionPerson,
    section: MentionCandidate['section'],
    formatName: (name: string | null | undefined) => string,
): MentionCandidate {
    return plainMentionCandidate({
        type,
        id: person.id,
        label: formatName(person.name),
        detail: person.office_name ?? null,
        token: `[[${type}:${person.id}]]`,
        section,
        tag: null,
    });
}

/** One row per person, keeping the first sighting. */
function personRows(
    type: 'caregiver' | 'client',
    people: ReadonlyArray<MentionPerson>,
    formatName: (name: string | null | undefined) => string,
): ReadonlyArray<SearchableMentionCandidate> {
    const byId = new Map<string, MentionPerson>();
    for (const person of people) {
        if (!byId.has(person.id)) {
            byId.set(person.id, person);
        }
    }
    return [...byId.values()].map((person) => ({
        candidate: personCandidate(type, person, 'outreach', formatName),
        searchText: person.name,
    }));
}

export function buildOutreachMentionCandidates(
    outreach: OutreachMentionSource,
    timeZone: string,
    formatName: (name: string | null | undefined) => string,
): ReadonlyArray<SearchableMentionCandidate> {
    const groupsEnabled = outreach.agent_generation === 'v3';
    const units = buildOutreachDisplayUnits(
        outreach.shifts,
        outreach.shift_groups,
    );
    const groupNumberById = new Map(
        outreach.shift_groups.map((group, index) => [group.id, index + 1]),
    );
    const shiftRows: Array<SearchableMentionCandidate> = [];
    const groupRows: Array<SearchableMentionCandidate> = [];
    const memberRows: Array<SearchableMentionCandidate> = [];
    for (const unit of units) {
        const visibleShifts = unit.shifts.filter(
            (shift) => shift.status !== 'missing',
        );
        if (unit.kind !== 'group' || !groupsEnabled) {
            for (const shift of visibleShifts) {
                shiftRows.push(
                    shiftCandidate(
                        shift,
                        timeZone,
                        'outreach',
                        null,
                        formatName,
                    ),
                );
            }
            continue;
        }
        const groupNumber = groupNumberById.get(unit.id);
        const clientNames = [
            ...new Set(
                unit.shifts.map((shift) => formatName(shift.patient_name)),
            ),
        ];
        const subject = groupReferenceSubject({
            clientNames,
            shiftCount: unit.shifts.length,
        });
        const label = groupReferenceLabel({
            clientNames,
            shiftCount: unit.shifts.length,
            groupMode: unit.groupMode,
        });
        const detail = formatDateSpan(
            unit.shifts.map((shift) => shift.shift_start_time),
            timeZone,
        );
        const memberDates = unit.shifts
            .map((shift) => formatShiftDate(shift.shift_start_time, timeZone))
            .join(' ');
        groupRows.push({
            candidate: {
                type: 'group',
                id: unit.id,
                label,
                subject,
                detail,
                token: `[[group:${unit.id}]]`,
                section: 'outreach',
                tag: groupNumber === undefined ? null : `Group ${groupNumber}`,
                groupMode: unit.groupMode,
            },
            searchText: `${label} ${detail ?? ''} group ${groupNumber ?? ''} ${memberDates}`,
        });
        for (const shift of visibleShifts) {
            memberRows.push(
                shiftCandidate(
                    shift,
                    timeZone,
                    'outreach',
                    groupNumber === undefined ? null : `Group ${groupNumber}`,
                    formatName,
                ),
            );
        }
    }
    // The outreach's people follow its work: caregivers on its shifts,
    // originally assigned, or staged, then the clients its shifts are for.
    const caregivers = [
        ...outreach.shifts.flatMap((shift) => shift.caregiver ?? []),
        ...Object.values(outreach.original_caregivers),
        ...(outreach.suggested_caregivers ?? []).map((staged) => ({
            id: staged.caregiver_id,
            name: staged.caregiver_name,
        })),
    ];
    const clients = outreach.shifts.flatMap((shift) =>
        shift.client_id === null || shift.client_id === undefined
            ? []
            : [{ id: shift.client_id, name: shift.patient_name }],
    );
    return [
        ...groupRows,
        ...shiftRows,
        ...memberRows,
        ...personRows('caregiver', caregivers, formatName),
        ...personRows('client', clients, formatName),
    ];
}
